import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink, useParams, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Snackbar,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import IosShareRoundedIcon from "@mui/icons-material/IosShareRounded";
import { supabase } from "../supabaseClient";
import { tokens } from "../theme";
import Markdown from "./Markdown";
import { fadeUp } from "./ui/motion";
import { PillChips } from "./ui/Pills";
import { SITE } from "../utils/shareCard";

/** "Cambridge, ON - Cambridge Youth" -> "Cambridge". */
export const jobCity = (location) => {
  const raw = String(location || "").split(",")[0].split(" - ")[0].trim();
  return raw || "Other";
};

const citySlug = (city) => city.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export const applicantLabel = (count) =>
  count === 0 ? "Be the first to apply" : count === 1 ? "1 person has applied" : `${count} people have applied`;

export const jobUrl = (job) => `https://${SITE}/jobs/${job.slug}`;

const JOB_COLUMNS =
  "id, slug, title, summary, description, location, pay, duration, employment_type, status, closes_on, questions, resume_required, created_at";

const RESUME_BUCKET = "job-resumes";
// Mirrors the job-resumes bucket: 10 MB, PDF or Word only.
const RESUME_MAX_BYTES = 10 * 1024 * 1024;
const RESUME_TYPES = {
  pdf: "application/pdf",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
};

const uuid = () => {
  const c = typeof window !== "undefined" ? window.crypto : undefined;
  if (c?.randomUUID) return c.randomUUID();
  const bytes = new Uint8Array(16);
  if (c?.getRandomValues) c.getRandomValues(bytes);
  else for (let i = 0; i < 16; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(`${value}T12:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
};

const optionLabel = (opt) => (typeof opt === "string" ? opt : opt?.label ?? opt?.value ?? "");
const optionValue = (opt) => (typeof opt === "string" ? opt : String(opt?.value ?? opt?.label ?? ""));

/** Normalizes the free-form `type` stored on each question in `jobs.questions`. */
const questionKind = (q) => {
  const type = String(q?.type || "short").toLowerCase();
  const hasOptions = Array.isArray(q?.options) && q.options.length > 0;
  if (["yesno", "yes_no", "boolean"].includes(type)) return "yesno";
  if (["long", "paragraph", "textarea"].includes(type)) return "long";
  if (["multi", "multiselect", "multi_select", "checkbox", "checkboxes"].includes(type)) return hasOptions ? "multi" : "short";
  if (["select", "dropdown"].includes(type)) return hasOptions ? "select" : "short";
  if (["choice", "single", "radio", "single_choice"].includes(type)) return hasOptions ? "radio" : "short";
  if (["number", "email", "url", "date", "tel"].includes(type)) return type;
  return "short";
};

const Fact = ({ icon: Icon, label, value }) =>
  value ? (
    <Box sx={{ display: "flex", gap: 1.25, alignItems: "flex-start", minWidth: 0 }}>
      <Box sx={{ width: 34, height: 34, borderRadius: 2.5, bgcolor: tokens.subtle, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon sx={{ fontSize: 19, color: tokens.ink }} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: "0.7rem", fontWeight: 800, letterSpacing: 1, textTransform: "uppercase", color: tokens.muted }}>{label}</Typography>
        <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", lineHeight: 1.35 }}>{value}</Typography>
      </Box>
    </Box>
  ) : null;

const QuestionField = ({ question, value, onChange, error }) => {
  const kind = questionKind(question);
  const label = question.label || "Question";
  const required = Boolean(question.required);
  const options = Array.isArray(question.options) ? question.options : [];

  if (kind === "yesno" || kind === "radio") {
    const choices = kind === "yesno" ? ["Yes", "No"] : options;
    return (
      <FormControl required={required} error={Boolean(error)}>
        <FormLabel sx={{ fontWeight: 700, color: tokens.text, "&.Mui-focused": { color: tokens.text } }}>{label}</FormLabel>
        <RadioGroup row={kind === "yesno"} value={value || ""} onChange={(e) => onChange(e.target.value)}>
          {choices.map((opt) => (
            <FormControlLabel key={optionValue(opt)} value={optionValue(opt)} control={<Radio />} label={optionLabel(opt)} />
          ))}
        </RadioGroup>
        {error && <FormHelperText>{error}</FormHelperText>}
      </FormControl>
    );
  }

  if (kind === "multi") {
    const selected = value ? value.split(", ").filter(Boolean) : [];
    const toggle = (v) => {
      const next = selected.includes(v) ? selected.filter((s) => s !== v) : [...selected, v];
      onChange(options.map(optionValue).filter((o) => next.includes(o)).join(", "));
    };
    return (
      <FormControl required={required} error={Boolean(error)} component="fieldset">
        <FormLabel component="legend" sx={{ fontWeight: 700, color: tokens.text, "&.Mui-focused": { color: tokens.text } }}>{label}</FormLabel>
        <FormGroup>
          {options.map((opt) => (
            <FormControlLabel
              key={optionValue(opt)}
              control={<Checkbox checked={selected.includes(optionValue(opt))} onChange={() => toggle(optionValue(opt))} />}
              label={optionLabel(opt)}
            />
          ))}
        </FormGroup>
        {error && <FormHelperText>{error}</FormHelperText>}
      </FormControl>
    );
  }

  const common = {
    label,
    required,
    fullWidth: true,
    value: value || "",
    onChange: (e) => onChange(e.target.value),
    error: Boolean(error),
    helperText: error || question.help || undefined,
    placeholder: question.placeholder || undefined,
    inputProps: { maxLength: 5000 },
  };

  if (kind === "select") {
    return (
      <TextField {...common} select>
        {options.map((opt) => (
          <MenuItem key={optionValue(opt)} value={optionValue(opt)}>
            {optionLabel(opt)}
          </MenuItem>
        ))}
      </TextField>
    );
  }
  if (kind === "long") return <TextField {...common} multiline minRows={4} />;
  if (kind === "date") return <TextField {...common} type="date" InputLabelProps={{ shrink: true }} />;
  if (kind === "short") return <TextField {...common} />;
  return <TextField {...common} type={kind} />;
};

const ApplyForm = ({ job, onSubmitted }) => {
  const questions = useMemo(() => (Array.isArray(job.questions) ? job.questions.filter((q) => q?.id) : []), [job.questions]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [answers, setAnswers] = useState({});
  const [resume, setResume] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const fileInput = useRef(null);
  const isOpen = job.status === "open";

  const pickResume = (file) => {
    setFieldErrors((prev) => ({ ...prev, resume: undefined }));
    if (!file) return setResume(null);
    const ext = file.name.split(".").pop().toLowerCase();
    if (!RESUME_TYPES[ext]) {
      setFieldErrors((prev) => ({ ...prev, resume: "Please attach a PDF or Word document (.pdf, .doc, .docx)." }));
      return setResume(null);
    }
    if (file.size > RESUME_MAX_BYTES) {
      setFieldErrors((prev) => ({ ...prev, resume: "Resume must be 10 MB or smaller." }));
      return setResume(null);
    }
    setResume(file);
  };

  const validate = () => {
    const errs = {};
    if (name.trim().length < 2) errs.name = "Please enter your full name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim())) errs.email = "Please enter a valid email address.";
    questions.forEach((q) => {
      if (q.required && !String(answers[q.id] || "").trim()) errs[q.id] = "This question is required.";
    });
    if (job.resume_required && !resume) errs.resume = "Please attach your resume.";
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!validate()) return;
    setSubmitting(true);
    try {
      let resumePath = null;
      if (resume) {
        const ext = resume.name.split(".").pop().toLowerCase();
        resumePath = `${job.id}/${uuid()}.${ext}`;
        const { error: uploadError } = await supabase.storage
          .from(RESUME_BUCKET)
          .upload(resumePath, resume, { contentType: RESUME_TYPES[ext], upsert: false });
        if (uploadError) throw new Error("Your resume could not be uploaded. Please try again, or apply without it.");
      }
      const { error: rpcError } = await supabase.rpc("submit_job_application", {
        p_job_id: job.id,
        p_name: name.trim(),
        p_email: email.trim(),
        p_phone: phone.trim() || null,
        p_answers: answers,
        p_resume_path: resumePath,
        p_resume_name: resume ? resume.name : null,
      });
      if (rpcError) throw new Error(rpcError.message || "Your application could not be submitted.");
      setSubmitted(true);
      onSubmitted?.();
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <Box sx={{ textAlign: "center", py: 4, px: 2, bgcolor: tokens.subtle, borderRadius: 3 }}>
        <CheckCircleIcon sx={{ fontSize: 44, color: tokens.win }} />
        <Typography sx={{ fontWeight: 900, fontSize: "1.2rem", mt: 1 }}>Application received</Typography>
        <Typography sx={{ color: tokens.muted, mt: 0.5 }}>
          Thanks, {name.trim().split(" ")[0]}. We'll be in touch at {email.trim().toLowerCase()}.
        </Typography>
      </Box>
    );
  }

  return (
    <Box component="form" noValidate onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 2.25 }}>
      {!isOpen && (
        <Alert severity="info">This listing is a {job.status} preview, so applications are turned off until it is opened.</Alert>
      )}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
        <TextField label="Full name" required value={name} onChange={(e) => setName(e.target.value)} error={Boolean(fieldErrors.name)} helperText={fieldErrors.name} autoComplete="name" inputProps={{ maxLength: 200 }} />
        <TextField label="Email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} error={Boolean(fieldErrors.email)} helperText={fieldErrors.email} autoComplete="email" inputProps={{ maxLength: 254 }} />
        <TextField label="Phone (optional)" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} autoComplete="tel" inputProps={{ maxLength: 40 }} />
      </Box>

      {questions.map((q) => (
        <QuestionField
          key={q.id}
          question={q}
          value={answers[q.id]}
          error={fieldErrors[q.id]}
          onChange={(v) => {
            setAnswers((prev) => ({ ...prev, [q.id]: v }));
            setFieldErrors((prev) => ({ ...prev, [q.id]: undefined }));
          }}
        />
      ))}

      <Box>
        <Typography sx={{ fontWeight: 700, mb: 0.75 }}>
          Resume{" "}
          <Box component="span" sx={{ color: tokens.muted, fontWeight: 500 }}>
            {job.resume_required ? "(required)" : "(optional)"}
          </Box>
        </Typography>
        <input
          ref={fileInput}
          type="file"
          accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          hidden
          onChange={(e) => pickResume(e.target.files?.[0] || null)}
        />
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          <Button variant="outlined" startIcon={<AttachFileIcon />} onClick={() => fileInput.current?.click()} sx={{ borderRadius: 999, borderColor: tokens.border, color: tokens.ink }}>
            {resume ? "Replace file" : "Attach resume"}
          </Button>
          {resume && (
            <>
              <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, wordBreak: "break-all" }}>{resume.name}</Typography>
              <Button
                size="small"
                onClick={() => {
                  setResume(null);
                  if (fileInput.current) fileInput.current.value = "";
                }}
                sx={{ color: tokens.muted }}
              >
                Remove
              </Button>
            </>
          )}
        </Box>
        <Typography sx={{ fontSize: "0.8rem", color: fieldErrors.resume ? tokens.loss : tokens.muted, mt: 0.75 }}>
          {fieldErrors.resume || "PDF or Word document, up to 10 MB."}
        </Typography>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}

      <Box>
        <Button
          type="submit"
          variant="contained"
          disabled={submitting || !isOpen}
          sx={{ backgroundColor: "#0a0a0a", fontWeight: 800, borderRadius: 999, px: 4, py: 1.2, "&:hover": { backgroundColor: "#222" } }}
        >
          {submitting ? "Submitting…" : "Submit application"}
        </Button>
      </Box>
    </Box>
  );
};

const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const area = document.createElement("textarea");
    area.value = text;
    area.setAttribute("readonly", "");
    area.style.position = "fixed";
    area.style.opacity = "0";
    document.body.appendChild(area);
    area.select();
    let ok = false;
    try {
      ok = document.execCommand("copy");
    } catch {
      ok = false;
    }
    document.body.removeChild(area);
    return ok;
  }
};

/** Opens the native share sheet for a job's link on phones, or copies it elsewhere. */
const JobShareButton = ({ job }) => {
  const [notice, setNotice] = useState("");
  const url = jobUrl(job);

  const onClick = async () => {
    // Desktop browsers that expose navigator.share often have no share targets, so only phones get the sheet.
    const touch = typeof window.matchMedia === "function" && window.matchMedia("(pointer: coarse)").matches;
    if (touch && navigator.share) {
      try {
        await navigator.share({ title: job.title, text: job.summary || job.title, url });
        return;
      } catch (e) {
        if (e?.name === "AbortError") return;
      }
    }
    setNotice((await copyText(url)) ? "Link copied" : url);
  };

  return (
    <>
      <Button
        size="small"
        onClick={onClick}
        aria-label={`Share ${job.title}`}
        startIcon={<IosShareRoundedIcon sx={{ fontSize: 17 }} />}
        sx={{ fontWeight: 800, borderRadius: 999, px: 1.5, color: tokens.ink, bgcolor: tokens.subtle, border: "1px solid #e4e4e7", "&:hover": { bgcolor: "#e4e4e7" } }}
      >
        Share
      </Button>
      <Snackbar open={!!notice} autoHideDuration={notice === "Link copied" ? 2500 : 8000} onClose={() => setNotice("")} message={notice} />
    </>
  );
};

const JobCard = ({ job, index, startOpen, applicants, onApplied }) => {
  const [applying, setApplying] = useState(Boolean(startOpen));

  return (
    <Box
      id={job.slug}
      component="article"
      sx={{ scrollMarginTop: 80, bgcolor: "#fff", border: `1px solid ${tokens.border}`, borderRadius: 4, p: { xs: 2.25, sm: 3.5 }, boxShadow: tokens.shadow.card, ...fadeUp(index) }}
    >
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center", mb: 1 }}>
        {job.employment_type && <Chip size="small" label={job.employment_type} sx={{ fontWeight: 700, bgcolor: tokens.subtle }} />}
        <Chip size="small" label={jobCity(job.location)} sx={{ fontWeight: 700, bgcolor: tokens.subtle }} />
        {job.status !== "open" && <Chip size="small" color="warning" label={`${job.status} · admin preview`} sx={{ fontWeight: 700, textTransform: "capitalize" }} />}
        <Box sx={{ ml: "auto" }}>
          <JobShareButton job={job} />
        </Box>
      </Box>
      <Typography component="h2" sx={{ fontWeight: 900, fontSize: { xs: "1.35rem", sm: "1.6rem" }, letterSpacing: "-0.02em", lineHeight: 1.15 }}>
        <Box component={RouterLink} to={`/jobs/${job.slug}`} sx={{ color: "inherit", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}>
          {job.title}
        </Box>
      </Typography>
      {job.summary && <Typography sx={{ color: tokens.muted, mt: 0.75, fontSize: "1.02rem" }}>{job.summary}</Typography>}

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, mt: 2.5 }}>
        <Fact icon={PaymentsOutlinedIcon} label="Pay" value={job.pay} />
        <Fact icon={PlaceOutlinedIcon} label="Location" value={job.location} />
        <Fact icon={ScheduleOutlinedIcon} label="Schedule" value={job.duration} />
        <Fact icon={WorkOutlineIcon} label="Type" value={job.employment_type} />
        <Fact icon={EventOutlinedIcon} label="Apply by" value={formatDate(job.closes_on)} />
        <Fact icon={PeopleOutlineIcon} label="Applicants" value={typeof applicants === "number" ? applicantLabel(applicants) : null} />
      </Box>

      {job.description && (
        <Box sx={{ mt: 2.5, pt: 2, borderTop: `1px solid ${tokens.border}` }}>
          <Markdown source={job.description} />
        </Box>
      )}

      <Box sx={{ mt: 2.5, pt: 2.5, borderTop: `1px solid ${tokens.border}` }}>
        {applying ? (
          <>
            <Typography component="h3" sx={{ fontWeight: 900, fontSize: "1.15rem", mb: 2 }}>Apply for this role</Typography>
            <ApplyForm job={job} onSubmitted={onApplied} />
          </>
        ) : (
          <Button
            variant="contained"
            onClick={() => setApplying(true)}
            sx={{ backgroundColor: "#0a0a0a", fontWeight: 800, borderRadius: 999, px: 4, py: 1.2, "&:hover": { backgroundColor: "#222" } }}
          >
            Apply now
          </Button>
        )}
      </Box>
    </Box>
  );
};

/**
 * Public job board at `/jobs` (and `/jobs/:slug`). Listings come from `public.jobs`
 * (RLS only exposes open, unexpired rows to the public); applications go through
 * the `submit_job_application` RPC, with resumes uploaded to the private
 * `job-resumes` bucket first. Applicant counts come from the `job_applicant_counts`
 * RPC, since `job_applications` itself is admin-only.
 */
const Jobs = () => {
  const { slug } = useParams();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [applicantCounts, setApplicantCounts] = useState({});

  useEffect(() => {
    let cancelled = false;
    supabase
      .from("jobs")
      .select(JOB_COLUMNS)
      .order("created_at", { ascending: true })
      .then(({ data, error: fetchError }) => {
        if (cancelled) return;
        if (fetchError) setError("Could not load job openings. Please refresh to try again.");
        else setJobs(data || []);
        setLoading(false);
      });
    supabase.rpc("job_applicant_counts").then(({ data, error: countError }) => {
      if (cancelled || countError || !Array.isArray(data)) return;
      setApplicantCounts(Object.fromEntries(data.map((row) => [row.job_id, Number(row.applicants) || 0])));
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const [searchParams, setSearchParams] = useSearchParams();
  const cityParam = (searchParams.get("city") || "").toLowerCase();

  const cities = useMemo(() => {
    const counts = new Map();
    jobs.forEach((j) => {
      const city = jobCity(j.location);
      counts.set(city, (counts.get(city) || 0) + 1);
    });
    return [...counts.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([name, count]) => ({ name, slug: citySlug(name), count }));
  }, [jobs]);

  const activeCity = cities.find((c) => c.slug === cityParam);
  const setCity = (value) => {
    const next = new URLSearchParams(searchParams);
    if (value === "all") next.delete("city");
    else next.set("city", value);
    setSearchParams(next, { replace: true });
  };

  const visible = slug
    ? jobs.filter((j) => j.slug === slug)
    : cityParam
      ? jobs.filter((j) => citySlug(jobCity(j.location)) === cityParam)
      : jobs;
  const showCityFilter = !slug && cities.length > 1;

  return (
    <Box sx={{ maxWidth: 860, mx: "auto", px: { xs: 0, sm: 2 }, pt: { xs: 0, sm: 3 }, pb: 6 }}>
      <Box sx={{ bgcolor: "#0a0a0a", color: "#fff", borderRadius: { xs: 0, sm: 4 }, px: { xs: 2.5, sm: 4 }, py: { xs: 3.5, sm: 5 }, position: "relative", overflow: "hidden" }}>
        <Box sx={{ position: "absolute", right: -80, top: -80, width: 260, height: 260, borderRadius: "50%", background: "radial-gradient(circle, rgba(255,255,255,0.14), rgba(255,255,255,0) 70%)" }} />
        <Typography sx={{ fontSize: "0.72rem", fontWeight: 800, letterSpacing: 2, textTransform: "uppercase", opacity: 0.65 }}>HADAF League</Typography>
        <Typography component="h1" sx={{ fontSize: { xs: "2rem", sm: "2.8rem" }, fontWeight: 900, letterSpacing: "-0.03em", lineHeight: 1.05, mt: 0.5 }}>
          Work with us
        </Typography>
        <Typography sx={{ opacity: 0.7, mt: 1.25, maxWidth: 560 }}>
          Help us run and grow the league. Browse open roles below and apply in a couple of minutes.
        </Typography>
        {slug && (
          <Button component={RouterLink} to="/jobs" size="small" sx={{ mt: 2, color: "#fff", fontWeight: 700, borderRadius: 999, border: "1px solid rgba(255,255,255,0.3)", px: 1.75 }}>
            All openings
          </Button>
        )}
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2, px: { xs: 1.5, sm: 0 } }}>
        {error && <Alert severity="error">{error}</Alert>}
        {showCityFilter && (
          <PillChips
            ariaLabel="Filter jobs by city"
            value={activeCity ? activeCity.slug : cityParam ? "" : "all"}
            onChange={setCity}
            options={[
              { value: "all", label: `All cities (${jobs.length})` },
              ...cities.map((c) => ({ value: c.slug, label: `${c.name} (${c.count})` })),
            ]}
          />
        )}
        {loading ? (
          [0, 1].map((i) => <Skeleton key={i} variant="rounded" height={320} sx={{ borderRadius: 4 }} />)
        ) : visible.length === 0 && !error ? (
          <Box sx={{ bgcolor: "#fff", border: `1px solid ${tokens.border}`, borderRadius: 4, p: 4, textAlign: "center" }}>
            <Typography sx={{ fontWeight: 900, fontSize: "1.15rem" }}>
              {slug ? "This job isn't available" : cityParam && jobs.length ? "No open roles in this city" : "No open roles right now"}
            </Typography>
            <Typography sx={{ color: tokens.muted, mt: 0.5 }}>
              {slug ? "It may have been filled or closed." : "Check back soon, or follow @hadafsoccerleague for updates."}
            </Typography>
            {(slug || (cityParam && jobs.length > 0)) && (
              <Button component={RouterLink} to="/jobs" sx={{ mt: 2, fontWeight: 700 }}>
                See all openings
              </Button>
            )}
          </Box>
        ) : (
          visible.map((job, i) => (
            <JobCard
              key={job.id}
              job={job}
              index={i}
              startOpen={Boolean(slug)}
              applicants={applicantCounts[job.id]}
              onApplied={() => setApplicantCounts((prev) => (job.id in prev ? { ...prev, [job.id]: prev[job.id] + 1 } : prev))}
            />
          ))
        )}
      </Box>
    </Box>
  );
};

export default Jobs;
